'use client'

import React, { useCallback, useMemo, useRef, useState } from 'react'
import Image from 'next/image'
import dynamic from 'next/dynamic'
import { Play } from 'lucide-react'

import type { Media } from '@/payload-types'
import { getMediaUrl } from '@/utilities/getMediaUrl'
import { getOptimizedImageUrl } from '@/utilities/getOptimizedImageUrl'
import { cn } from '@/utilities/ui'
import { ProgressiveMatterport } from '@/components/ProgressiveMatterport'

// Lightbox only renders on open (`{lightboxIndex !== null && …}`) — code-split it
// so its chunk loads on first open instead of inflating the initial bundle.
const ImageLightbox = dynamic(
  () => import('@/components/ImageLightbox').then((m) => m.ImageLightbox),
  { ssr: false },
)

type Props = {
  iframeEmbed?: string | null
  showIframePlayOverlay?: boolean | null
  videoUrl?: string | null
  videoPoster?: string | null
  videoAutoplay?: boolean | null
  galleryImages?: { image?: number | Media | null }[] | null
  galleryAutoSlide?: boolean | null
  galleryIntervalMs?: number | null
}

function MediaCard({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={cn('h-full overflow-hidden bg-white', className)}>{children}</div>
}

export function HomepageLatestCraftedProject({
  iframeEmbed,
  videoUrl,
  videoPoster,
  videoAutoplay,
  galleryImages,
}: Props) {
  const images = useMemo(
    () =>
      (galleryImages ?? [])
        .map((item) => {
          const image = item?.image
          if (!image || typeof image !== 'object') return ''
          const raw = image.url ?? (image.filename ? `/media/${image.filename}` : '')
          return raw ? getMediaUrl(raw) : ''
        })
        .filter(Boolean),
    [galleryImages],
  )
  const hasImages = images.length > 0

  const gallerySlots = useMemo(() => {
    return 15
  }, [])

  const overflowCount = Math.max(0, images.length - gallerySlots)
  const visibleTiles = images.slice(0, gallerySlots)
  const placeholderCount = Math.max(0, gallerySlots - visibleTiles.length)

  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)
  const [videoStarted, setVideoStarted] = useState(false)
  const videoRef = useRef<HTMLVideoElement | null>(null)

  const closeLightbox = useCallback(() => setLightboxIndex(null), [])

  return (
    <section className="container-fluid mx-auto px-5 pt-[50px] pb-[20px] md:pt-[40px] md:pb-0 overflow-x-hidden">
      <div className="mb-[20px] md:ml-[10px] md:mb-[30px] w-full md:w-[25%]">
        <h2
          className="font-medium text-[32px] leading-[1.2em]"
          style={{ color: 'rgb(29,29,27)', fontFamily: 'var(--font-outfit-family), sans-serif' }}
        >
          Our Latest
          <br />
          Crafted Project
        </h2>
        <div aria-hidden className="h-[10px] bg-[#009bab] w-full md:w-[calc(25vw-25px)]" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-[20px] xl:grid-cols-5">
        <MediaCard>
          <div className="group relative w-full overflow-hidden bg-black h-[400px] md:h-[520px] xl:h-[514px]">
            {iframeEmbed ? (
              // Progressive: poster + CTA on load; Matterport JS/iframe only on
              // interaction or near-viewport idle (keeps it off the critical path).
              <ProgressiveMatterport
                embedHtml={iframeEmbed}
                posterFallback={images[0] ?? null}
                className="h-full w-full"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-sm text-white/70">
                Add iframe embed HTML
              </div>
            )}
          </div>
        </MediaCard>

        <MediaCard>
          <div className="relative w-full overflow-hidden bg-black h-[684px] md:h-[520px] xl:h-[514px]">
            {videoUrl ? (
              <div className="relative h-full w-full">
                <video
                  className="h-full w-full object-cover"
                  controls={videoStarted}
                  playsInline
                  autoPlay={Boolean(videoAutoplay)}
                  muted={Boolean(videoAutoplay)}
                  loop={Boolean(videoAutoplay)}
                  preload="metadata"
                  poster={getOptimizedImageUrl(videoPoster) || undefined}
                  ref={videoRef}
                >
                  <source src={videoUrl} />
                </video>
                {!videoStarted && (
                  <button
                    type="button"
                    aria-label="Play video"
                    className="absolute inset-0 flex items-center justify-center cursor-pointer"
                    onClick={() => {
                      setVideoStarted(true)
                      videoRef.current?.play().catch(() => {})
                    }}
                  >
                    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white/85">
                      <Play className="ml-0.5 h-5 w-5 fill-[#1d1d1b] text-[#1d1d1b]" />
                    </span>
                  </button>
                )}
              </div>
            ) : (
              <div className="flex h-full w-full items-center justify-center text-sm text-white/70">
                Add a video URL
              </div>
            )}
          </div>
        </MediaCard>

        <MediaCard className="md:col-span-2 xl:col-span-3">
          <div className="h-full p-0">
            {hasImages ? (
              <>
                <div className="h-auto overflow-hidden xl:h-[514px]">
                  <div className="grid h-full w-full grid-cols-3 grid-rows-5 gap-[10px] xl:grid-cols-5 xl:grid-rows-3 xl:gap-[6px]">
                    {visibleTiles.map((src, index) => {
                      const isLastVisible = index === visibleTiles.length - 1
                      const showMore = overflowCount > 0 && isLastVisible
                      const openIndex = showMore ? gallerySlots : index
                      return (
                        <button
                          key={`${src}-${index}`}
                          type="button"
                          onClick={() => {
                            setLightboxIndex(openIndex)
                          }}
                          className={cn(
                            'group/thumb relative min-h-0 min-w-0 overflow-hidden bg-[#e8e8e8] text-left outline-none lg:aspect-auto',
                            'aspect-square',
                            'ring-offset-2 transition-[transform,box-shadow] duration-300 ease-out xl:aspect-auto',
                            'hover:z-1 focus-visible:z-1 focus-visible:ring-2 focus-visible:ring-[#1595a7]',
                            'hover:shadow-md active:scale-[0.98]',
                          )}
                          aria-label={
                            showMore
                              ? `Open gallery, ${overflowCount} more images`
                              : `Open image ${index + 1} in gallery`
                          }
                        >
                          <Image
                            src={src}
                            alt="Project image"
                            fill
                            sizes="(max-width: 768px) 33vw, (max-width: 1280px) 25vw, 220px"
                            className="object-cover transition-transform duration-500 ease-out group-hover/thumb:scale-105"
                          />
                          <span className="pointer-events-none absolute inset-0 bg-black/0 transition-colors duration-300 group-hover/thumb:bg-black/35 group-focus-visible/thumb:bg-black/25" />
                          {showMore ? (
                            <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/45 text-2xl font-medium tracking-tight text-white transition-opacity duration-300">
                              +{overflowCount}
                            </span>
                          ) : null}
                        </button>
                      )
                    })}
                    {Array.from({ length: placeholderCount }).map((_, i) => (
                      <div
                        key={`empty-${i}`}
                        className="min-h-0 min-w-0 bg-[#f0f0f0]"
                        aria-hidden
                      />
                    ))}
                  </div>
                </div>

                {lightboxIndex !== null && (
                  <ImageLightbox
                    images={images.map((src, i) => ({ src, alt: `Gallery image ${i + 1}` }))}
                    startIndex={lightboxIndex}
                    onClose={closeLightbox}
                  />
                )}
              </>
            ) : (
              <div className="flex h-[240px] items-center justify-center bg-[#f0f0f0] text-sm text-[#69727d] sm:h-[320px] xl:h-[514px]">
                Add gallery images
              </div>
            )}
          </div>
        </MediaCard>
      </div>
    </section>
  )
}
